import { TextField, InputAdornment, Stack, Button, Box, ToggleButtonGroup, ToggleButton } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import useBundleTranslation from 'i18n';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import { FormControlChooseIconProps, FormElementControlPropsType } from 'components/common/form/layout/control';
import { RawFormElementProps } from 'components/common/form';
import ReactHookFormController from 'components/common/form/layout/ReactHookFormController';
import { useWatch } from 'react-hook-form';
import faIconListJson from './icon-list-data/fa-list.json';
import IconMi from 'components/common/icon/IconMi';
import IconHandler from 'components/common/icon/IconHandler';
import { prepareFormElementProps } from 'components/common/form/formTools';
import StaticInfo from 'components/common/StaticInfo';
import styles from './ChooseIcon.styles';
import { IconMiNameList } from 'components/common/icon/IconMiData';
import { FormComponentBuilder } from 'components/common/form/layout';

type FormChooseIconIconType = 'none' | 'upload' | 'fa' | 'mi';

interface ControlValueIconType {
    type: FormChooseIconIconType;
    value: string;
}

export default function ChooseIcon({ controlProps: props }: FormElementControlPropsType<FormControlChooseIconProps>) {
    const FIELDS_KEY = {
        TYPE: `${props.name}_type`,
        CODE: `${props.name}_code`,
        IMAGE: `${props.name}`,
        INIT_DATA: `${props.name}_init`,
        SIZE: `${props.name}_size`,
    };

    const iconSet = useMemo(() => {
        const availableIconSet = ['mi', 'fa'];
        const result = (props?.iconSet ?? []).filter((name) => {
            return availableIconSet.indexOf(name) !== -1;
        });
        if (result.length > 0) {
            return result;
        } else {
            return ['fa'];
        }
    }, [props?.iconSet]);

    const { t } = useBundleTranslation(['components/common/choose_icon']);
    const [isShowPopup, setIsShowPopup] = useState<boolean>(false);
    const [currentIconSet, setCurrentIconSet] = useState<string>(iconSet[0]);
    const [defaultIconType, setDefaultIconType] = useState<FormChooseIconIconType>(props?.defaultIcon?.type ?? '');
    const [defaultIconCode, setDefaultIconCode] = useState<string>(props?.defaultIcon?.value ?? '');
    const [iconType, setIconType] = useState<FormChooseIconIconType>(
        props.form.hookFormGetValues(FIELDS_KEY.INIT_DATA)?.type ?? 'none',
    );
    const [iconCode, setIconCode] = useState<string>(props.form.hookFormGetValues(FIELDS_KEY.INIT_DATA)?.value ?? '');
    const [searchKeyword, setSearchKeyword] = useState<string>('');
    const labelForFileFieldRef = useRef<HTMLLabelElement | null>(null);

    const [uploadKey, setUploadKey] = useState<string>('initial');
    const [uploadedFileHolder, setUploadedFileHolder] = useState<any>('');
    const iconsContainerRef = useRef<HTMLLabelElement | null>(null);

    const resetIconListScroll = () => {
        if (iconsContainerRef.current) {
            iconsContainerRef.current.scrollTo({
                top: 0,
                behavior: 'instant',
            });
        }
    };

    const iconsList: any = useMemo(() => {
        const data: any = {
            fa: faIconListJson,
            mi: IconMiNameList.filter((item) => {
                return ![
                    'avatar',
                    'tile-chart',
                    'tile',
                    'element-preview',
                    'full-chart',
                    'live-dashboard',
                    'viewer',
                ].includes(item);
            }),
        };

        resetIconListScroll();

        const searchString = (searchKeyword || '').toLowerCase().trim();

        return data[currentIconSet].filter((item: any) => {
            return item.includes(searchString);
        });
    }, [currentIconSet, searchKeyword]);

    useEffect(() => {
        if (props.size === null) {
            return;
        }

        props.form.hookFormSetValue(FIELDS_KEY.SIZE, props.size, { shouldDirty: false });
    }, [props.size]);

    const choosePopupSettings: PopupSettings = {
        title: t('popup_title'),
        textOK: t('popup_button'),
        noCancel: true,
    };

    const closePopup = () => {
        //re-setup preview image if exist
        setIsShowPopup(false);
        props.form.hookFormSetValue(FIELDS_KEY.IMAGE, uploadedFileHolder, { shouldDirty: false });
        if (uploadedFileHolder?.length) {
            setIconCode(URL.createObjectURL(uploadedFileHolder[0]));
        }
    };

    const selectAndClosePopup = () => {
        setIsShowPopup(false);
    };

    const selectIcon = (iconType: any, iconCode: string) => {
        setIconType(iconType);
        setIconCode(iconCode);
        resetImageField();
        selectAndClosePopup();
    };

    const removeIcon = () => {
        if (defaultIconType && defaultIconCode) {
            setIconType(defaultIconType);
            setIconCode(defaultIconCode);
        } else {
            setIconType('none');
            setIconCode('');
        }
        resetImageField();
    };

    const resetImageField = () => {
        props.form.hookFormSetValue(FIELDS_KEY.IMAGE, '', { shouldDirty: false });
        setUploadKey('key_' + Date.now());
    };

    const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchKeyword(event.target.value);
    };

    const resetSearch = () => {
        setSearchKeyword('');
    };

    // Watch file change
    useWatch({ name: FIELDS_KEY.IMAGE, control: props.form.hookFormControl });
    // Bind file 'onChange' trigger
    const uploadField = props.form.hookFormWatch(FIELDS_KEY.IMAGE);
    useEffect(() => {
        if (isShowPopup) {
            selectAndClosePopup();
            if (uploadField?.length) {
                setIconType('upload');
                setUploadedFileHolder(uploadField);
                setIconCode(URL.createObjectURL(uploadField[0]));
            } else {
                setIconType('none');
            }
        }
    }, [uploadField]);

    const elementProps = {
        data: {},
        form: props.form,
    } as RawFormElementProps;

    //workaround to trigger dirty
    useEffect(() => {
        const initialSet = !props.form.hookFormGetValues(FIELDS_KEY.TYPE);
        props.form.hookFormSetValue(FIELDS_KEY.TYPE, iconType, { shouldDirty: !initialSet });
    }, [iconType]);

    useEffect(() => {
        const initialSet = !props.form.hookFormGetValues(FIELDS_KEY.CODE);
        props.form.hookFormSetValue(FIELDS_KEY.CODE, iconCode, { shouldDirty: !initialSet });
    }, [iconCode]);

    useEffect(() => {
        if (!isShowPopup) {
            setCurrentIconSet(iconSet[0]);
            setSearchKeyword('');
            resetIconListScroll();
        }
    }, [isShowPopup]);

    return (
        <Box>
            <ReactHookFormController
                elementProps={prepareFormElementProps({
                    ...elementProps,
                    component: { uid: FIELDS_KEY.TYPE, name: FIELDS_KEY.TYPE, component: 'FormHidden' },
                })}
            />
            <ReactHookFormController
                elementProps={prepareFormElementProps({
                    ...elementProps,
                    component: { uid: FIELDS_KEY.CODE, name: FIELDS_KEY.CODE, component: 'FormHidden' },
                })}
            />

            <Stack direction="row" alignItems="center">
                <Box>
                    {(iconType === 'fa' || iconType === 'mi') && (
                        <Box sx={styles.previewIcon} className={`icon-type--${iconType}`}>
                            <IconHandler icon={{ type: iconType, value: iconCode }} />
                        </Box>
                    )}
                    <ReactHookFormController
                        key={uploadKey}
                        elementProps={prepareFormElementProps({
                            ...elementProps,
                            component: {
                                uid: FIELDS_KEY.IMAGE,
                                name: FIELDS_KEY.IMAGE,
                                component: 'UploadImage',
                                props: {
                                    hidePreview: iconType !== 'upload',
                                    previewsList: [iconCode],
                                    ignoreStateField: true,
                                    id: FIELDS_KEY.IMAGE,
                                    sx: styles.image,
                                },
                            },
                        })}
                    />
                </Box>
                <Button
                    onClick={() => setIsShowPopup(true)}
                    variant="outlined"
                    startIcon={iconType !== 'none' ? <IconMi icon={'edit-pencil'} fontSize={'16'} /> : undefined}
                >
                    {t(iconType !== 'none' ? 'button_filled_state' : 'button_empty_state')}
                </Button>
                {iconType !== 'none' &&
                    (!defaultIconType || iconType != defaultIconType || iconCode != defaultIconCode) && (
                        <Button
                            variant="outlined"
                            sx={{ ml: 1 }}
                            onClick={removeIcon}
                            className={'min-width--icon'}
                            color={'neutral'}
                        >
                            <IconMi icon="delete" fontSize={'16'} />
                        </Button>
                    )}
            </Stack>

            <Popup
                settings={choosePopupSettings}
                open={isShowPopup}
                onHide={() => closePopup()}
                onConfirm={() => closePopup()}
                keepMounted
                maxWidth="popupMd"
            >
                {iconSet.length > 1 && (
                    <Box sx={{ mb: 2 }}>
                        <Box sx={{ pb: 0.5 }}>{t('icon_set_label')}</Box>
                        <ToggleButtonGroup
                            value={currentIconSet}
                            exclusive
                            onChange={(event, value) => {
                                if (value !== null) {
                                    setCurrentIconSet(value);
                                }
                            }}
                        >
                            {iconSet.map((iconSetItem) => {
                                return (
                                    <ToggleButton value={iconSetItem} key={iconSetItem} sx={{ minWidth: '80px' }}>
                                        {t(`icon_set_title.${iconSetItem}`)}
                                    </ToggleButton>
                                );
                            })}
                        </ToggleButtonGroup>
                    </Box>
                )}

                <Box sx={{ mb: 2 }}>
                    <TextField
                        onChange={onSearch}
                        value={searchKeyword}
                        fullWidth
                        InputProps={{
                            startAdornment: !searchKeyword ? (
                                <InputAdornment position="start">
                                    <IconMi icon="search" fontSize={'16'} sx={{ color: 'primary.main' }} />
                                </InputAdornment>
                            ) : (
                                <InputAdornment
                                    position="start"
                                    onClick={resetSearch}
                                    sx={{ color: 'primary.main', '&:hover': { opacity: 0.85 } }}
                                >
                                    <IconMi icon="times" fontSize={'16'} sx={{ color: 'primary.main' }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                <Box sx={styles.iconsContainer} ref={iconsContainerRef}>
                    {iconsList.length == 0 && searchKeyword > '' && (
                        <Box sx={styles.emptySearchMsg}>{t('search_no_data_found_placeholder')}</Box>
                    )}
                    {iconsList.map((item: string) => {
                        return (
                            <Box
                                onClick={() => selectIcon(currentIconSet, item)}
                                key={`${currentIconSet}-${item}`}
                                className={`icon-type--${currentIconSet}`}
                                sx={styles.iconItem}
                            >
                                <IconHandler icon={{ type: currentIconSet, value: item }} />
                            </Box>
                        );
                    })}
                </Box>

                <Box sx={{ my: 2 }}>
                    <Button
                        variant="light"
                        onClick={() => {
                            setUploadKey('key_' + Date.now());
                            setTimeout(() => {
                                labelForFileFieldRef?.current?.click();
                            }, 100);
                        }}
                        startIcon={<IconMi icon="upload" fontSize={'16'} />}
                    >
                        {t('popup_upload_button')}
                    </Button>
                    <label htmlFor={FIELDS_KEY.IMAGE} ref={labelForFileFieldRef} />
                </Box>

                <Box>
                    <StaticInfo label={t('popup_upload_note')} />
                </Box>
            </Popup>
        </Box>
    );
}

export class ChooseIconBuilder extends FormComponentBuilder {
    processFormData(formData: FormData, key: string, data: any) {
        const files = Object.values(data).length ? Object.values(data) : [data];
        files.forEach((file: any) => {
            formData.append(key, file);
        });
    }
    prepareProps(): FormControlChooseIconProps {
        return {
            ...this.controlProps,
            size: this.elementProps.componentProps?.fetchOnTab,
            defaultIcon: this.elementProps.componentProps?.defaultIcon ?? null,
            iconSet: this.elementProps.componentProps?.iconSet ?? ['fa'],
        };
    }
}
